import { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/api/client';
import toast from 'react-hot-toast';

// Dynamic import for Vapi SDK (to avoid SSR issues)
let Vapi: any = null;
const loadVapi = async () => {
  if (typeof window !== 'undefined' && !Vapi) {
    try {
      const module = await import('@vapi-ai/web');
      Vapi = module.default || module;
    } catch (error) {
      console.error('Failed to load Vapi SDK:', error);
    }
  }
  return Vapi;
};

interface PhoneInterfaceProps {
  assistantId?: string;
  onCallEnd?: () => void;
}

const PhoneInterface = ({ assistantId, onCallEnd }: PhoneInterfaceProps) => {
  const [isCalling, setIsCalling] = useState(false);
  const [callStatus, setCallStatus] = useState<'idle' | 'connecting' | 'connected' | 'ended'>('idle');
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const vapiRef = useRef<any>(null);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (vapiRef.current) {
        try {
          vapiRef.current.stop();
        } catch (e) {
          // Ignore errors during cleanup
        }
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []);

  const startCall = async () => {
    // Load Vapi SDK if not already loaded
    const VapiClass = await loadVapi();
    if (!VapiClass) {
      toast.error('Failed to load Vapi SDK. Please refresh the page.');
      return;
    }

    setIsCalling(true);
    setError(null);
    setCallStatus('connecting');

    try {
      // Get web call configuration from backend
      const response = await apiClient.createWebCall(assistantId);
      
      if (!response.public_key || !response.assistant_id) {
        throw new Error('Invalid call configuration from server');
      }

      // Initialize Vapi with public key
      // Vapi.ai Web SDK can be initialized with just the public key
      // or with both key and assistantId in the start method
      const vapi = new VapiClass(response.public_key);
      vapiRef.current = vapi;

      // Set up event handlers BEFORE starting the call
      vapi.on('call-start', () => {
        setIsCalling(false);
        setCallStatus('connected');
        toast.success('Call connected!');
        
        // Start duration timer
        let seconds = 0;
        durationIntervalRef.current = setInterval(() => {
          seconds++;
          setCallDuration(seconds);
        }, 1000);
      });

      vapi.on('call-end', () => {
        setIsCalling(false);
        setCallStatus('ended');
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
        }
        toast('Call ended');
        if (onCallEnd) {
          onCallEnd();
        }
      });

      vapi.on('error', (error: any) => {
        console.error('Vapi error:', error);
        setError(error.message || 'Call error occurred');
        setIsCalling(false);
        setCallStatus('idle');
        toast.error(error.message || 'Call failed');
      });

      vapi.on('message', (message: any) => {
        console.log('Vapi message:', message);
      });

      // Start the call with assistant ID
      // Vapi.ai Web SDK expects assistantId in the start method
      await vapi.start({
        assistantId: response.assistant_id,
      });

    } catch (error: any) {
      console.error('Error starting call:', error);
      setError(error.message || 'Failed to start call');
      setIsCalling(false);
      setCallStatus('idle');
      toast.error(error.message || 'Failed to start call');
    }
  };

  const endCall = () => {
    if (vapiRef.current) {
      try {
        vapiRef.current.stop();
      } catch (e) {
        console.error('Error ending call:', e);
      }
    }
    setIsCalling(false);
    setCallStatus('ended');
    setCallDuration(0);
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    if (onCallEnd) {
      onCallEnd();
    }
  };

  const toggleMute = () => {
    if (vapiRef.current) {
      try {
        if (isMuted) {
          vapiRef.current.unmute();
        } else {
          vapiRef.current.mute();
        }
        setIsMuted(!isMuted);
      } catch (e) {
        console.error('Error toggling mute:', e);
      }
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="phone-interface-container" style={{ maxWidth: '400px', margin: '0 auto' }}>
      {/* Phone Display */}
      <div 
        className="phone-screen border-30 bg-white"
        style={{
          borderRadius: '40px',
          padding: '40px 20px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          background: callStatus === 'connected' 
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          color: callStatus === 'connected' ? 'white' : '#333',
          minHeight: '600px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        {/* Status Bar */}
        <div className="status-bar text-center mb-4">
          <div className="d-flex align-items-center justify-content-center gap-2 mb-2">
            <div 
              className="status-indicator"
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: callStatus === 'connected' 
                  ? '#4ade80' 
                  : callStatus === 'connecting' 
                  ? '#fbbf24' 
                  : '#ef4444',
                animation: callStatus === 'connecting' ? 'pulse 2s infinite' : 'none',
              }}
            />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>
              {callStatus === 'idle' && 'Ready to call'}
              {callStatus === 'connecting' && 'Connecting...'}
              {callStatus === 'connected' && 'Connected'}
              {callStatus === 'ended' && 'Call ended'}
            </span>
          </div>
          {callStatus === 'connected' && (
            <div style={{ fontSize: '24px', fontWeight: '700' }}>
              {formatDuration(callDuration)}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="phone-content flex-fill d-flex flex-column align-items-center justify-content-center">
          {callStatus === 'idle' && (
            <div className="text-center">
              <div 
                className="mb-4"
                style={{
                  fontSize: '80px',
                  marginBottom: '20px',
                }}
              >
                📞
              </div>
              <h3 style={{ marginBottom: '10px', fontWeight: '600' }}>
                Ajebota Voice Assistant
              </h3>
              <p style={{ opacity: 0.8, fontSize: '14px' }}>
                Click to start a conversation with your assistant
              </p>
            </div>
          )}

          {callStatus === 'connecting' && (
            <div className="text-center">
              <div 
                className="spinner-border text-light mb-4"
                role="status"
                style={{ width: '60px', height: '60px' }}
              >
                <span className="visually-hidden">Connecting...</span>
              </div>
              <p>Connecting to assistant...</p>
            </div>
          )}

          {callStatus === 'connected' && (
            <div className="text-center">
              <div 
                className="mb-4"
                style={{
                  fontSize: '80px',
                  animation: 'pulse 2s infinite',
                }}
              >
                🎙️
              </div>
              <h4 style={{ marginBottom: '10px' }}>Call in Progress</h4>
              <p style={{ opacity: 0.9, fontSize: '14px' }}>
                Speak naturally with your assistant
              </p>
            </div>
          )}

          {callStatus === 'ended' && (
            <div className="text-center">
              <div className="mb-4" style={{ fontSize: '80px' }}>
                ✅
              </div>
              <h4>Call Ended</h4>
              <p style={{ opacity: 0.8, fontSize: '14px' }}>
                Thank you for using Ajebota Voice Assistant
              </p>
            </div>
          )}

          {error && (
            <div className="alert alert-danger mt-3" style={{ fontSize: '12px' }}>
              {error}
            </div>
          )}
        </div>

        {/* Control Buttons */}
        <div className="phone-controls d-flex justify-content-center gap-4 mt-4">
          {callStatus === 'idle' && (
            <button
              className="btn btn-success rounded-circle"
              onClick={startCall}
              style={{
                width: '70px',
                height: '70px',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              }}
              disabled={isCalling}
            >
              📞
            </button>
          )}

          {callStatus === 'connecting' && (
            <button
              className="btn btn-danger rounded-circle"
              onClick={endCall}
              style={{
                width: '70px',
                height: '70px',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
              }}
            >
              📞
            </button>
          )}

          {callStatus === 'connected' && (
            <>
              <button
                className={`btn rounded-circle ${isMuted ? 'btn-warning' : 'btn-light'}`}
                onClick={toggleMute}
                style={{
                  width: '60px',
                  height: '60px',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? '🔇' : '🎤'}
              </button>
              <button
                className="btn btn-danger rounded-circle"
                onClick={endCall}
                style={{
                  width: '70px',
                  height: '70px',
                  fontSize: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                }}
              >
                📞
              </button>
            </>
          )}

          {callStatus === 'ended' && (
            <button
              className="btn btn-primary rounded-circle"
              onClick={() => {
                setCallStatus('idle');
                setCallDuration(0);
                setError(null);
              }}
              style={{
                width: '70px',
                height: '70px',
                fontSize: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              🔄
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default PhoneInterface;

